import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { QueryProvider } from '@/lib/query'
import { AuthGuard } from '@/components/auth'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { MainLayout } from '@/components/layout'
import { LoginPage } from '@/pages/auth'
import { DashboardPage } from '@/pages/dashboard'
import { UsersListPage, DepartmentsPage, RolesPage } from '@/pages/users'
import { DistributorsPage } from '@/pages/distributors'
import { CustomerGiftsPage, CustomersPage } from '@/pages/customers'
import { ServiceRecordsPage } from '@/pages/customers/service-records'
import { ServiceStatisticsPage } from '@/pages/customers/service-statistics'
import { DepartmentServiceStatisticsPage } from '@/pages/customers/department-service-statistics'
import { SupplierOrdersPage } from '@/pages/supplier/orders'
import { SupplierOrderDetailPage } from '@/pages/supplier/orders/[id]'
import { SupplierRefundsPage } from '@/pages/supplier/refunds'
import { RefundDetailPage } from '@/pages/supplier/refunds/[id]'
import { SupplierDepositPage } from '@/pages/supplier/deposit'
import { MyCommissionsPage, DepartmentCommissionsPage } from '@/pages/commissions'
import { EmployeeOrdersPage } from '@/pages/employee/orders'
import { EmployeeManualOrdersPage } from '@/pages/employee/manual-orders'
import { CreateOrderPage } from '@/pages/admin/orders/create'
import { ProfilePage } from '@/pages/profile/ProfilePage'
import { AccountPage } from '@/pages/settings/AccountPage'
import { SystemSettingsPage } from '@/pages/settings/SystemSettingsPage'
import { PromotionBundlesPage } from '@/pages/settings/promotion-bundles'
import { CustomerCategoriesPage } from '@/pages/settings/customer-categories'
import { ServiceTypesPage } from '@/pages/settings/service-types'
import { AuditLogsPage } from '@/pages/settings/audit-logs'
import { GiftProductsPage } from '@/pages/store/gift-products'
import { MaterialsPage } from '@/pages/store/materials'
import { ProductsPage } from '@/pages/store/products'
import { DepartmentSummaryPage, RecordsPage, UserSummaryPage, UserWeightPage } from '@/pages/data-cube'
import {
  PerformanceListPage,
  EmployeePerformancePage,
  UnitPerformancePage,
  PromotionPerformancePage,
  PerformanceRankingPage,
  PerformanceSummaryPage,
} from '@/pages/performance'
import { VibeKanbanWebCompanion } from 'vibe-kanban-web-companion'

function AppRoutes() {
	return (
		<Routes>
			{/* 公开路由 - 登录页 */}
			<Route path="/login" element={<LoginPage />} />

			{/* 受保护路由 - 需要登录 */}
			<Route
				path="/"
				element={
					<AuthGuard>
						<MainLayout />
					</AuthGuard>
				}
			>
				<Route index element={<Navigate to="/dashboard" replace />} />
				<Route path="dashboard" element={<DashboardPage />} />
				<Route path="users/list" element={<UsersListPage />} />
				<Route path="users/departments" element={<DepartmentsPage />} />
				<Route path="users/departments/:deptId" element={<DepartmentsPage />} />
				<Route path="users/roles" element={<RolesPage />} />
				<Route path="users/distributors" element={<DistributorsPage />} />
				<Route path="customers" element={<CustomersPage />} />
				<Route path="customers/gifts" element={<CustomerGiftsPage />} />
				<Route path="customers/service-records" element={<ServiceRecordsPage />} />
				<Route path="customers/service-statistics" element={<ServiceStatisticsPage />} />
				<Route path="customers/department-service-statistics" element={<DepartmentServiceStatisticsPage />} />
				{/* 供应商路由 */}
				<Route path="supplier/orders" element={<SupplierOrdersPage />} />
				<Route path="supplier/orders/:id" element={<SupplierOrderDetailPage />} />
				<Route path="supplier/refunds" element={<SupplierRefundsPage />} />
				<Route path="supplier/refunds/:id" element={<RefundDetailPage />} />
				<Route path="supplier/deposit" element={<SupplierDepositPage />} />
				<Route path="commissions/my" element={<MyCommissionsPage />} />
				<Route path="commissions/department" element={<DepartmentCommissionsPage />} />
				<Route path="data-cube/records" element={<RecordsPage />} />
				<Route path="data-cube/user-summary" element={<UserSummaryPage />} />
				<Route path="data-cube/user-weight" element={<UserWeightPage />} />
				<Route path="data-cube/department-summary" element={<DepartmentSummaryPage />} />
				<Route path="employee/orders" element={<EmployeeOrdersPage />} />
				<Route path="employee/manual-orders" element={<EmployeeManualOrdersPage />} />
				{/* 代客下单 */}
				<Route path="admin/orders/create" element={<CreateOrderPage />} />
				{/* 仓库管理 */}
				<Route path="store/gift-products" element={<GiftProductsPage />} />
				<Route path="store/materials" element={<MaterialsPage />} />
				<Route path="store/products" element={<ProductsPage />} />
				{/* 个人中心 */}
				<Route path="profile" element={<ProfilePage />} />
				{/* 账户设置 */}
				<Route path="settings/account" element={<AccountPage />} />
				{/* 系统设置 */}
				<Route path="settings/system" element={<SystemSettingsPage />} />
				<Route path="settings/customer-categories" element={<CustomerCategoriesPage />} />
				<Route path="settings/service-types" element={<ServiceTypesPage />} />
				<Route path="settings/promotion-bundles" element={<PromotionBundlesPage />} />
				<Route path="settings/audit-logs" element={<AuditLogsPage />} />
				{/* 业绩管理 */}
				<Route path="performances" element={<PerformanceListPage />} />
				<Route path="performances/employee" element={<EmployeePerformancePage />} />
				<Route path="performances/unit" element={<UnitPerformancePage />} />
				<Route path="performances/promotion" element={<PromotionPerformancePage />} />
				<Route path="performances/ranking" element={<PerformanceRankingPage />} />
				<Route path="performances/summary" element={<PerformanceSummaryPage />} />
			</Route>

			{/* 404 重定向 */}
			<Route path="*" element={<Navigate to="/dashboard" replace />} />
		</Routes>
	)
}

export function App() {
	return (
		<QueryProvider>
			<Toaster richColors position="top-right" />
			<VibeKanbanWebCompanion />
			<BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
				<ErrorBoundary>
					<AppRoutes />
				</ErrorBoundary>
			</BrowserRouter>
		</QueryProvider>
	)
}
